import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { CheckCircle, CloudUpload, DeleteOutline, Palette, Replay, SmartToy, Wallpaper } from '@mui/icons-material';
import { useSetAtom } from 'jotai';

import { DEFAULT_CONFIG } from '../../models/data/ChatbotConfig';
import { getChatbotConfig, saveChatbotConfig, uploadLogo } from '../../utils/server/storageUtils';
import { getActiveChatbotId, getActiveTenantId } from '../../utils/server/authUtils';
import { showToastError, showToastSuccess } from '../../utils/common/toast';
import { chatbotConfigState } from '../../state/config/ChatbotConfigState';
import { useAppTranslation } from '../../hooks/useAppTranslation';
import { useAuth } from '../../../context/AuthContext';
import wallpaper1 from '../../../assets/wallpaper-1.png';
import wallpaper2 from '../../../assets/wallpaper-2.png';
import wallpaper3 from '../../../assets/wallpaper-3.png';


function isValidUuid(value) {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        String(value || '').trim()
    );
}

function sanitizeChatbotId(value = null) {
    return isValidUuid(value) ? String(value).trim() : null;
}

const FALLBACK_PRIMARY = '#172983';
const FALLBACK_SECONDARY = '#068945';
const FALLBACK_WALLPAPER = 'none';
const DEFAULT_FORM_VALUES = {
    ...DEFAULT_CONFIG,
    modelType: 'fast',
    primaryColor: FALLBACK_PRIMARY,
    secondaryColor: FALLBACK_SECONDARY,
    chatWallpaper: FALLBACK_WALLPAPER,
};

const LANGUAGE_OPTIONS = [
    { value: 'de', labelKey: 'languageOptionGerman' },
    { value: 'en', labelKey: 'languageOptionEnglish' },
];

const FONT_OPTIONS = [
    'Roboto',
    'Inter',
    'Poppins',
    'Open Sans',
    'Lato',
    'Nunito',
    'Montserrat',
    'DM Sans',
    'Work Sans',
    'Source Sans Pro',
    'Fira Sans',
    'Ubuntu',
    'Segoe UI',
    'Helvetica Neue',
    'System UI',
    'Arial',
    'Verdana',
    'Trebuchet MS',
    'Tahoma',
    'Georgia',
    'Merriweather',
    'Playfair Display',
    'Times New Roman',
    'Courier New',
];

const CHAT_WALLPAPER_OPTIONS = [
    {
        value: 'wallpaper_1',
        labelKey: 'wallpaper1Label',
        descriptionKey: 'wallpaper1Desc',
        assetPath: wallpaper1,
        fallback: 'linear-gradient(135deg, rgba(59,130,246,0.28) 0%, rgba(99,102,241,0.2) 40%, rgba(255,255,255,0.18) 100%)',
    },
    {
        value: 'wallpaper_2',
        labelKey: 'wallpaper2Label',
        descriptionKey: 'wallpaper2Desc',
        assetPath: wallpaper2,
        fallback: 'linear-gradient(135deg, rgba(16,185,129,0.24) 0%, rgba(6,182,212,0.18) 45%, rgba(255,255,255,0.16) 100%)',
    },
    {
        value: 'wallpaper_3',
        labelKey: 'wallpaper3Label',
        descriptionKey: 'wallpaper3Desc',
        assetPath: wallpaper3,
        fallback: 'linear-gradient(135deg, rgba(244,114,182,0.22) 0%, rgba(168,85,247,0.18) 48%, rgba(255,255,255,0.16) 100%)',
    },
    {
        value: 'none',
        labelKey: 'wallpaperNoneLabel',
        descriptionKey: 'wallpaperNoneDesc',
        assetPath: '',
        fallback: 'linear-gradient(180deg, rgba(248,250,252,0.98) 0%, rgba(241,245,249,0.96) 100%)',
        isPlain: true,
    },
];

function getWallpaperOption(value) {
    return CHAT_WALLPAPER_OPTIONS.find((item) => item.value === value) || CHAT_WALLPAPER_OPTIONS[CHAT_WALLPAPER_OPTIONS.length - 1];
}

function getWallpaperPreviewStyles(value) {
    const option = getWallpaperOption(value);

    if (option.isPlain || !option.assetPath) {
        return {
            backgroundImage: option.fallback,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
        };
    }

    // FIXED: Removed white gradient overlay to show original dark image
    return {
        backgroundImage: `url("${option.assetPath}")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
    };
}

const fieldSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '16px',
        backgroundColor: 'var(--surface-fill-strong)',
        '& fieldset': {
            borderColor: 'var(--border-color)',
        },
        '&:hover fieldset': {
            borderColor: 'var(--text-muted)',
        },
        '&.Mui-focused fieldset': {
            borderWidth: '1px',
            borderColor: 'var(--accent-color)',
        },
    },
    '& .MuiInputLabel-root.Mui-focused': {
        color: 'var(--accent-color)',
    },
};

const sharedSelectMenuProps = {
    PaperProps: {
        sx: {
            borderRadius: '16px',
            maxHeight: 'min(34vh, 220px)',
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            border: '1px solid var(--border-color)',
            boxShadow: '0 24px 60px rgba(15,23,42,0.18)',
        },
    },
    MenuListProps: {
        sx: {
            py: 0.5,
        },
    },
};

export function ChatbotCustomizationPanel() {
    const setGlobalConfig = useSetAtom(chatbotConfigState);
    const { t } = useAppTranslation();
    const { token } = useAuth();
    const activeTenantId = useMemo(() => (token ? getActiveTenantId({ token }) : null), [token]);
    const activeChatbotId = useMemo(
        () => sanitizeChatbotId(token ? getActiveChatbotId({ token, tenantId: activeTenantId }) : null),
        [activeTenantId, token]
    );
    const [isLoading, setIsLoading] = useState(false);
    const [isUploadingLogo, setIsUploadingLogo] = useState(false);
    const [persistedMeta, setPersistedMeta] = useState({
        hasCompletedOnboarding: false,
        onboardingCompletedAt: null,
        setupSource: null,
    });
    const [lastSyncedValues, setLastSyncedValues] = useState(DEFAULT_FORM_VALUES);

    const {
        register,
        control,
        handleSubmit,
        setValue,
        watch,
        getValues,
        reset,
        formState: { isDirty },
    } = useForm({
        defaultValues: DEFAULT_FORM_VALUES,
    });

    const currentPrimaryColor = watch('primaryColor');
    const currentSecondaryColor = watch('secondaryColor');
    const currentLogoUrl = watch('logoUrl');
    const currentChatbotName = watch('chatbotName');
    const currentFontFamily = watch('fontFamily') || 'Roboto';
    const currentChatWallpaper = watch('chatWallpaper') || FALLBACK_WALLPAPER;
    const isSaveDisabled = isLoading || isUploadingLogo || !isDirty;

    const wallpaperPreviewLabel = useMemo(() => t[getWallpaperOption(currentChatWallpaper).labelKey] || '', [currentChatWallpaper, t]);

    useEffect(() => {
        const subscription = watch((value) => {
            const liveConfig = { ...DEFAULT_CONFIG, ...persistedMeta, ...value };
            setGlobalConfig(liveConfig);
        });
        return () => subscription.unsubscribe();
    }, [watch, setGlobalConfig, persistedMeta]);

    useEffect(() => {
        const fetchConfig = async () => {
            if (!token) return;

            try {
                const config = await getChatbotConfig(token, activeChatbotId, activeTenantId);
                if (config) {
                    const nextMeta = {
                        hasCompletedOnboarding: Boolean(config.hasCompletedOnboarding),
                        onboardingCompletedAt: config.onboardingCompletedAt || null,
                        setupSource: config.setupSource || null,
                    };
                    const nextFormValues = {
                        ...DEFAULT_FORM_VALUES,
                        chatbotName: config.chatbotName || DEFAULT_FORM_VALUES.chatbotName,
                        primaryColor: config.primaryColor || FALLBACK_PRIMARY,
                        secondaryColor: config.secondaryColor || FALLBACK_SECONDARY,
                        fontFamily: config.fontFamily || 'Roboto',
                        logoUrl: config.logoUrl || '',
                        language: config.language || 'de',
                        modelType: config.modelType || 'fast',
                        chatWallpaper: config.chatWallpaper || FALLBACK_WALLPAPER,
                    };

                    reset(nextFormValues);
                    setLastSyncedValues(nextFormValues);
                    setPersistedMeta(nextMeta);
                    setGlobalConfig({ ...DEFAULT_CONFIG, ...config, ...nextMeta });
                } else {
                    reset(DEFAULT_FORM_VALUES);
                    setLastSyncedValues(DEFAULT_FORM_VALUES);
                    setPersistedMeta({
                        hasCompletedOnboarding: false,
                        onboardingCompletedAt: null,
                        setupSource: null,
                    });
                    setGlobalConfig(DEFAULT_FORM_VALUES);
                }
            } catch (error) {
                console.error('Failed to load config', error);
            }
        };

        fetchConfig();
    }, [activeChatbotId, activeTenantId, reset, setGlobalConfig, token]);

    const handleResetCustomization = () => {
        reset(lastSyncedValues);
        setGlobalConfig({ ...DEFAULT_CONFIG, ...persistedMeta, ...lastSyncedValues });
    };

    const handleRemoveLogo = () => {
        setValue('logoUrl', '', { shouldDirty: true });
        const currentConfig = getValues();
        setGlobalConfig({ ...currentConfig, logoUrl: '' });
    };

    const handleLogoUpload = async (event) => {
        if (!event.target.files || event.target.files.length === 0) return;

        const file = event.target.files[0];

        if (file.size > 2 * 1024 * 1024) {
            showToastError(t.errorFileTooBig);
            event.target.value = '';
            return;
        }

        if (!token) {
            showToastError(t.authRequiredAgain);
            event.target.value = '';
            return;
        }

        setIsUploadingLogo(true);

        try {
            const formData = new FormData();
            formData.append('file', file);

            const downloadUrl = await uploadLogo(formData, token, activeChatbotId, activeTenantId);

            setValue('logoUrl', downloadUrl, { shouldDirty: true });
            const currentConfig = getValues();
            setGlobalConfig({ ...currentConfig, logoUrl: downloadUrl });

            showToastSuccess(t.successLogo);
        } catch (error) {
            console.error('Upload error:', error);
            showToastError(error.message || t.errorUploadGeneric);
        } finally {
            setIsUploadingLogo(false);
            event.target.value = '';
        }
    };

    const onSubmit = async (data) => {
        if (!token) {
            showToastError(t.authRequiredAgain);
            return;
        }

        setIsLoading(true);

        try {
            const configToSave = {
                ...DEFAULT_CONFIG,
                ...persistedMeta,
                ...data,
                modelType: data.modelType || 'fast',
                chatWallpaper: data.chatWallpaper || FALLBACK_WALLPAPER,
            };

            const saveResult = await saveChatbotConfig(token, configToSave, activeChatbotId, activeTenantId);
            const persistedConfig = {
                ...configToSave,
                ...(saveResult?.config || {}),
            };
            const nextMeta = {
                hasCompletedOnboarding: Boolean(persistedConfig.hasCompletedOnboarding),
                onboardingCompletedAt: persistedConfig.onboardingCompletedAt || null,
                setupSource: persistedConfig.setupSource || null,
            };

            const resetValues = {
                ...DEFAULT_FORM_VALUES,
                chatbotName: persistedConfig.chatbotName || DEFAULT_FORM_VALUES.chatbotName,
                primaryColor: persistedConfig.primaryColor || FALLBACK_PRIMARY,
                secondaryColor: persistedConfig.secondaryColor || FALLBACK_SECONDARY,
                fontFamily: persistedConfig.fontFamily || 'Roboto',
                logoUrl: persistedConfig.logoUrl || '',
                language: persistedConfig.language || 'de',
                modelType: persistedConfig.modelType || 'fast',
                chatWallpaper: persistedConfig.chatWallpaper || FALLBACK_WALLPAPER,
            };

            setPersistedMeta(nextMeta);
            reset(resetValues);
            setLastSyncedValues(resetValues);
            setGlobalConfig({ ...persistedConfig, ...nextMeta });
            showToastSuccess(t.successSaved);
        } catch (error) {
            console.error('Save config error:', error);
            showToastError(error.message || t.errorSaved);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2.25, md: 3 },
                borderRadius: '28px',
                border: '1px solid var(--border-color)',
                background: 'var(--surface-gradient)',
                boxShadow: '0 24px 48px rgba(15, 23, 42, 0.08)',
                backdropFilter: 'blur(16px)',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', lg: 'row' },
                    gap: 3,
                    alignItems: 'stretch',
                }}
            >
                <Box sx={{ flex: 1.35, minWidth: 0 }}>
                    <Box sx={{ mb: 2.5 }}>
                        <Typography variant='h5' sx={{ fontWeight: 800, color: 'var(--text-strong)', letterSpacing: '-0.02em' }}>
                            {t.adminTitle}
                        </Typography>
                        <Typography sx={{ mt: 0.75, color: 'var(--text-muted)', lineHeight: 1.7 }}>
                            {t.adminHeaderDescription}
                        </Typography>
                    </Box>

                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Box display='flex' flexDirection='column' gap={2}>
                            <Box display='flex' gap={2} flexDirection={{ xs: 'column', md: 'row' }}>
                                <TextField
                                    {...register('chatbotName')}
                                    label={t.chatbotName}
                                    fullWidth
                                    variant='outlined'
                                    sx={fieldSx}
                                />

                                <Controller
                                    name='language'
                                    control={control}
                                    render={({ field }) => (
                                        <FormControl fullWidth sx={fieldSx}>
                                            <InputLabel>{t.language}</InputLabel>
                                            <Select
                                                {...field}
                                                label={t.language}
                                                value={field.value || 'de'}
                                                MenuProps={sharedSelectMenuProps}
                                            >
                                                {LANGUAGE_OPTIONS.map((option) => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {t[option.labelKey]}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    )}
                                />
                            </Box>

                            <Box display='flex' gap={2} alignItems='center' flexDirection={{ xs: 'column', md: 'row' }}>
                                <Controller
                                    name='primaryColor'
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            label={t.primaryColor}
                                            type='color'
                                            fullWidth
                                            InputLabelProps={{ shrink: true }}
                                            sx={{
                                                ...fieldSx,
                                                '& input': { height: '52px', cursor: 'pointer' },
                                            }}
                                            value={field.value || FALLBACK_PRIMARY}
                                            onChange={(e) => field.onChange(e.target.value)}
                                        />
                                    )}
                                />

                                <Controller
                                    name='secondaryColor'
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            label={t.secondaryColor}
                                            type='color'
                                            fullWidth
                                            InputLabelProps={{ shrink: true }}
                                            sx={{
                                                ...fieldSx,
                                                '& input': { height: '52px', cursor: 'pointer' },
                                            }}
                                            value={field.value || FALLBACK_SECONDARY}
                                            onChange={(e) => field.onChange(e.target.value)}
                                        />
                                    )}
                                />
                            </Box>

                            <Box display='flex' gap={2} flexDirection={{ xs: 'column', md: 'row' }} alignItems='stretch'>
                                <Controller
                                    name='fontFamily'
                                    control={control}
                                    render={({ field }) => (
                                        <FormControl fullWidth sx={fieldSx}>
                                            <InputLabel>{t.fontFamily}</InputLabel>
                                            <Select
                                                {...field}
                                                label={t.fontFamily}
                                                value={field.value || 'Roboto'}
                                                MenuProps={sharedSelectMenuProps}
                                            >
                                                {FONT_OPTIONS.map((font) => (
                                                    <MenuItem key={font} value={font} sx={{ fontFamily: font }}>
                                                        {font}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    )}
                                />

                                <Box width='100%' display='flex' flexDirection='column' gap={1}>
                                    <Button
                                        component='label'
                                        variant='outlined'
                                        startIcon={isUploadingLogo ? <CircularProgress size={20} /> : <CloudUpload />}
                                        fullWidth
                                        sx={{
                                            height: '56px',
                                            borderRadius: '16px',
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            borderColor: 'var(--border-color)',
                                            color: 'var(--text-soft)',
                                            backgroundColor: 'var(--surface-fill)',
                                            '&:hover': {
                                                borderColor: 'var(--text-muted)',
                                                backgroundColor: 'var(--surface-muted)',
                                            },
                                        }}
                                        disabled={isUploadingLogo}
                                    >
                                        {isUploadingLogo ? t.uploading : t.logoUpload}
                                        <input
                                            type='file'
                                            hidden
                                            accept='image/*'
                                            onChange={handleLogoUpload}
                                        />
                                    </Button>

                                    <input type='hidden' {...register('logoUrl')} />

                                    {currentLogoUrl && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                mt: 0.25,
                                                px: 1.25,
                                                py: 1,
                                                borderRadius: '14px',
                                                border: '1px solid rgba(226, 232, 240, 1)',
                                                backgroundColor: 'var(--surface-fill)',
                                            }}
                                        >
                                            <Typography variant='caption' sx={{ color: 'var(--text-muted)', fontWeight: 700 }}>
                                                {t.preview}
                                            </Typography>
                                            <Box
                                                sx={{
                                                    width: 40,
                                                    height: 40,
                                                    borderRadius: '50%',
                                                    overflow: 'hidden',
                                                    border: '2px solid var(--border-color)',
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <img
                                                    src={currentLogoUrl}
                                                    alt='Logo Preview'
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                />
                                            </Box>
                                            <Button
                                                type='button'
                                                size='small'
                                                variant='text'
                                                startIcon={<DeleteOutline fontSize='small' />}
                                                onClick={handleRemoveLogo}
                                                sx={{
                                                    ml: 'auto',
                                                    minWidth: 'auto',
                                                    textTransform: 'none',
                                                    fontWeight: 700,
                                                    color: 'var(--text-muted)',
                                                }}
                                            >
                                                Remove
                                            </Button>
                                        </Box>
                                    )}
                                </Box>
                            </Box>

                            <Controller
                                name='chatWallpaper'
                                control={control}
                                render={({ field }) => (
                                    <Box
                                        sx={{
                                            p: { xs: 1.4, md: 1.8 },
                                            borderRadius: '20px',
                                            border: '1px solid var(--border-color)',
                                            background: 'var(--surface-gradient-soft)',
                                            boxShadow: '0 16px 32px rgba(15, 23, 42, 0.08)',
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.6 }}>
                                            <Wallpaper sx={{ color: 'var(--accent-color-strong)' }} />
                                            <Typography sx={{ fontWeight: 800, color: 'var(--text-strong)' }}>
                                                {t.wallpaperSectionTitle}
                                            </Typography>
                                        </Box>

                                        <Typography sx={{ color: 'var(--text-muted)', lineHeight: 1.7, mb: 1.5 }}>
                                            {t.wallpaperSectionDescription}
                                        </Typography>

                                        <Box
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', xl: 'repeat(4, 1fr)' },
                                                gap: 1.25,
                                            }}
                                        >
                                            {CHAT_WALLPAPER_OPTIONS.map((option) => {
                                                const isSelected = (field.value || FALLBACK_WALLPAPER) === option.value;
                                                return (
                                                    <Button
                                                        key={option.value}
                                                        type='button'
                                                        onClick={() => field.onChange(option.value)}
                                                        variant='outlined'
                                                        sx={{
                                                            p: 0,
                                                            borderRadius: '18px',
                                                            textTransform: 'none',
                                                            overflow: 'hidden',
                                                            alignItems: 'stretch',
                                                            justifyContent: 'stretch',
                                                            borderColor: isSelected ? 'var(--accent-color)' : 'var(--border-color)',
                                                            boxShadow: isSelected ? '0 0 0 3px var(--accent-glow)' : 'none',
                                                            '&:hover': {
                                                                borderColor: 'var(--accent-color)',
                                                                backgroundColor: 'transparent',
                                                            },
                                                        }}
                                                    >
                                                        <Box sx={{ width: '100%' }}>
                                                            <Box
                                                                sx={{
                                                                    height: 96,
                                                                    ...getWallpaperPreviewStyles(option.value),
                                                                }}
                                                            />
                                                            <Box
                                                                sx={{
                                                                    px: 1.2,
                                                                    py: 1,
                                                                    backgroundColor: 'var(--surface-fill-strong)',
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                    alignItems: 'center',
                                                                    gap: 1,
                                                                }}
                                                            >
                                                                <Box sx={{ textAlign: 'left', minWidth: 0 }}>
                                                                    <Typography sx={{ fontWeight: 800, color: 'var(--text-strong)', fontSize: '0.92rem' }}>
                                                                        {t[option.labelKey]}
                                                                    </Typography>
                                                                    <Typography sx={{ color: 'var(--text-muted)', fontSize: '0.76rem' }}>
                                                                        {t[option.descriptionKey]}
                                                                    </Typography>
                                                                </Box>
                                                                {isSelected && (
                                                                    <CheckCircle sx={{ color: 'var(--accent-color)', flexShrink: 0 }} />
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    </Button>
                                                );
                                            })}
                                        </Box>
                                    </Box>
                                )}
                            />

                            <Alert
                                severity={isDirty ? 'info' : 'success'}
                                variant='outlined'
                                sx={{
                                    mt: 0.25,
                                    borderRadius: '16px',
                                    borderColor: isDirty ? 'rgba(59,130,246,0.22)' : 'rgba(16,185,129,0.22)',
                                    backgroundColor: isDirty ? 'rgba(59,130,246,0.06)' : 'rgba(16,185,129,0.06)',
                                }}
                            >
                                {isDirty
                                    ? t.customizationStatusUnsaved
                                    : t.customizationStatusSaved}
                            </Alert>

                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={1.25}
                                alignItems={{ xs: 'stretch', sm: 'center' }}
                                justifyContent='space-between'
                            >
                                <Stack direction='row' spacing={1} alignItems='center' sx={{ flexWrap: 'wrap' }}>
                                    <Chip
                                        size='small'
                                        label={currentFontFamily || 'Roboto'}
                                        sx={{
                                            fontWeight: 700,
                                            borderRadius: '999px',
                                            backgroundColor: 'var(--surface-muted)',
                                            border: '1px solid var(--border-color)',
                                        }}
                                    />
                                    <Chip
                                        size='small'
                                        label={wallpaperPreviewLabel}
                                        sx={{
                                            fontWeight: 700,
                                            borderRadius: '999px',
                                            backgroundColor: 'var(--surface-muted)',
                                            border: '1px solid var(--border-color)',
                                        }}
                                    />
                                </Stack>

                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                                    <Button
                                        type='button'
                                        variant='outlined'
                                        size='large'
                                        disabled={isLoading || isUploadingLogo || !isDirty}
                                        startIcon={<Replay />}
                                        onClick={handleResetCustomization}
                                        sx={{
                                            minHeight: 54,
                                            borderRadius: '16px',
                                            fontWeight: 800,
                                            textTransform: 'none',
                                            borderColor: 'var(--border-color)',
                                            color: 'var(--text-soft)',
                                            '&:hover': {
                                                borderColor: 'var(--text-muted)',
                                                backgroundColor: 'var(--surface-muted)',
                                            },
                                        }}
                                    >
                                        {t.reset}
                                    </Button>

                                    <Button
                                        type='submit'
                                        variant='contained'
                                        size='large'
                                        disabled={isSaveDisabled}
                                        sx={{
                                            minHeight: 54,
                                            borderRadius: '16px',
                                            fontWeight: 800,
                                            textTransform: 'none',
                                            background: isDirty
                                                ? `linear-gradient(135deg, ${currentPrimaryColor || FALLBACK_PRIMARY} 0%, ${currentSecondaryColor || FALLBACK_SECONDARY} 100%)`
                                                : 'linear-gradient(135deg, rgba(148,163,184,0.16) 0%, rgba(148,163,184,0.1) 100%)',
                                            boxShadow: isDirty
                                                ? '0 0 0 1px rgba(255,255,255,0.08), 0 16px 32px var(--accent-shadow)'
                                                : 'none',
                                            '&.Mui-disabled': {
                                                color: 'rgba(15, 23, 42, 0.42)',
                                                background: 'rgba(148,163,184,0.12)',
                                            },
                                        }}
                                    >
                                        {isLoading ? t.saving : isDirty ? t.save : t.saved}
                                    </Button>
                                </Stack>
                            </Stack>
                        </Box>
                    </form>
                </Box>

                <Box sx={{ width: { xs: '100%', lg: 360 }, flexShrink: 0 }}>
                    <Box
                        sx={{
                            height: '100%',
                            p: 2.2,
                            borderRadius: '24px',
                            border: '1px solid var(--border-color)',
                            background: 'var(--surface-preview)',
                            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.8)',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                            <SmartToy sx={{ color: 'var(--accent-color)' }} />
                            <Typography sx={{ fontWeight: 800, color: 'var(--text-strong)' }}>{t.livePreview}</Typography>
                        </Box>

                        <Box
                            sx={{
                                p: 2,
                                borderRadius: '22px',
                                background: `linear-gradient(135deg, ${currentPrimaryColor || FALLBACK_PRIMARY} 0%, ${currentSecondaryColor || FALLBACK_SECONDARY} 100%)`,
                                boxShadow: '0 16px 32px rgba(15, 23, 42, 0.12)',
                                color: '#fff',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
                                <Box
                                    sx={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: '16px',
                                        backgroundColor: 'rgba(255,255,255,0.18)',
                                        border: '1px solid rgba(255,255,255,0.28)',
                                        display: 'grid',
                                        placeItems: 'center',
                                        overflow: 'hidden',
                                        flexShrink: 0,
                                    }}
                                >
                                    {currentLogoUrl ? (
                                        <img
                                            src={currentLogoUrl}
                                            alt={t.previewBotAlt}
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        />
                                    ) : (
                                        <SmartToy />
                                    )}
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography sx={{ fontWeight: 800 }} noWrap>
                                        {currentChatbotName || t.defaultChatbotName}
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.83rem', opacity: 0.88 }}>
                                        {t.previewAssistantStyle}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    p: 1.2,
                                    borderRadius: '18px',
                                    border: '1px solid rgba(255,255,255,0.18)',
                                    ...getWallpaperPreviewStyles(currentChatWallpaper),
                                }}
                            >
                                <Box
                                    sx={{
                                        p: 1.25,
                                        borderRadius: '14px',
                                        backgroundColor: 'rgba(255,255,255,0.62)',
                                        border: '1px solid rgba(255,255,255,0.22)',
                                        backdropFilter: 'blur(3px)',
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: '0.9rem',
                                            lineHeight: 1.65,
                                            fontFamily: currentFontFamily,
                                            color: '#0f172a',
                                        }}
                                    >
                                        {t.previewAssistantMessage}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                mt: 2,
                                p: 1.5,
                                borderRadius: '18px',
                                backgroundColor: 'var(--surface-muted)',
                                border: '1px solid var(--border-color)',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Palette sx={{ color: 'var(--text-muted)', fontSize: 18 }} />
                                <Typography sx={{ fontSize: '0.9rem', fontWeight: 800, color: 'var(--text-soft)' }}>
                                    {t.activePalette}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Box sx={{ flex: 1, height: 42, borderRadius: '12px', backgroundColor: currentPrimaryColor || FALLBACK_PRIMARY }} />
                                <Box sx={{ flex: 1, height: 42, borderRadius: '12px', backgroundColor: currentSecondaryColor || FALLBACK_SECONDARY }} />
                            </Box>

                            <Typography sx={{ mt: 1.15, color: 'var(--text-muted)', fontSize: '0.82rem', fontWeight: 700 }}>
                                {t.wallpaperLabel}: {wallpaperPreviewLabel}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Paper>
    );
}